import { persistLocationState, readPersistedLocationState, toHistoryEnvelope } from '../infra/locationStatePersistence';
import { sendErrReport, uploadFileToCloudStorage } from '../infra/utils';
import { toCsvBlob } from '../utils/csv.utils';
import { injectScriptOrLink } from './adapters/scriptInjector';
import { makeObserveElementVisibility } from './observeElementVisibility';
import { makeTrackPointerDrag } from './pointerDrag';
import { makePostJson } from './postJson';
import { makeOnViewportResize } from './viewportResize';
const isResourceTiming = (entry)=>entry.entryType === 'resource';
const findResourceTimingIn = (entries, urlFragment)=>{
    if (urlFragment.length === 0) return null;
    const matches = entries.filter(isResourceTiming).filter((candidate)=>candidate.name.includes(urlFragment));
    const entry = matches[matches.length - 1];
    if (entry == null) return null;
    // WHY: apigw is cross-origin and sets no Timing-Allow-Origin, so every other timing attribute reads 0.
    return {
        durationMs: entry.duration,
        responseEndMs: entry.responseEnd
    };
};
const toHex = (bytes)=>Array.from(bytes, (b)=>b.toString(16).padStart(2, '0')).join('');
export const makeBecameVisibleRegistrar = (doc)=>(onBecameVisible)=>{
        const handleVisibilityChange = ()=>doc.visibilityState === 'visible' && onBecameVisible();
        doc.addEventListener('visibilitychange', handleVisibilityChange);
        return ()=>doc.removeEventListener('visibilitychange', handleVisibilityChange);
    };
export const makePageRestoredRegistrar = (win)=>(onPageRestored)=>{
        const handlePageShow = (event)=>event.persisted && onPageRestored();
        win.addEventListener('pageshow', handlePageShow);
        return ()=>win.removeEventListener('pageshow', handlePageShow);
    };
export const makeNetworkEventRegistrar = (win, type)=>(onEvent)=>{
        const handleEvent = ()=>onEvent();
        win.addEventListener(type, handleEvent);
        return ()=>win.removeEventListener(type, handleEvent);
    };
export const scrollElementIntoView = function scrollElementIntoView(element) {
    let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    return element.scrollIntoView && element.scrollIntoView({
        behavior: options.disableScrollAnimation ? 'instant' : 'smooth',
        block: options.block || 'center'
    });
};
// WHY: not async, so a blocked download still throws synchronously into downloadCsv's callers as it did before saving became awaitable.
const makeSaveFile = (url, doc)=>(blob, filename)=>{
        const objectUrl = url.createObjectURL(blob);
        const anchor = doc.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        doc.body.appendChild(anchor);
        try {
            anchor.click();
        } finally{
            anchor.remove();
            url.revokeObjectURL(objectUrl);
        }
        return Promise.resolve();
    };
export const makeDownloadCsv = (saveFile)=>(csvOrCsvRows, filename)=>{
        // WHY: csv exports have no async error path, and the native saver already reports its own failures.
        saveFile(toCsvBlob(csvOrCsvRows), filename).catch(()=>undefined);
    };
const makeFetchBlob = (fetchFn)=>async (url)=>{
        const response = await fetchFn(url);
        if (!response.ok) throw new Error("Download failed: ".concat(response.status));
        return response.blob();
    };
const saveFileOf = (host, wiring)=>{
    var _wiring_saveFile;
    return (_wiring_saveFile = wiring.saveFile) !== null && _wiring_saveFile !== void 0 ? _wiring_saveFile : makeSaveFile(host.url, host.doc);
};
export const makeBrowserIo = (host, wiring)=>({
        locationReload: wiring.locationReload,
        readPageLoadStatus: ()=>({
                visibilityState: host.doc.visibilityState,
                online: host.nav.onLine,
                msSincePageLoad: host.perf.now()
            }),
        findResourceTiming: (urlFragment)=>findResourceTimingIn(host.perf.getEntriesByType('resource'), urlFragment),
        registerBecameVisibleListener: makeBecameVisibleRegistrar(host.doc),
        registerPageRestoredListener: makePageRestoredRegistrar(host.pageShowWin),
        registerOnlineListener: makeNetworkEventRegistrar(host.onlineWin, 'online'),
        registerOfflineListener: makeNetworkEventRegistrar(host.onlineWin, 'offline'),
        getPortalRoot: ()=>host.portalRoot,
        observeElementVisibility: makeObserveElementVisibility(host.visibilityObserverCtor),
        reportError: sendErrReport,
        changeLocation: wiring.changeLocation,
        getOrigin: ()=>host.win.location.origin,
        getUserAgent: ()=>host.nav.userAgent,
        // WHY: real browsers report false; stealth email-link scanners delete the flag, leaving it undefined.
        isAutomated: ()=>host.nav.webdriver !== false,
        copyToClipboard: (text)=>host.nav.clipboard.writeText(text),
        openNewTab: wiring.openNewTab,
        openPendingTab: wiring.openPendingTab,
        downloadCsv: makeDownloadCsv(saveFileOf(host, wiring)),
        saveFile: saveFileOf(host, wiring),
        fetchBlob: makeFetchBlob(host.fetch),
        injectScriptOrLink,
        uploadFileToCloudStorage,
        scrollIntoView: scrollElementIntoView,
        getBoundingClientRect: (element)=>element.getBoundingClientRect(),
        afterNextPaint: (callback)=>host.win.requestAnimationFrame(()=>host.win.requestAnimationFrame(()=>callback())),
        schedule: (ms, callback)=>{
            const timer = setTimeout(callback, ms);
            return ()=>clearTimeout(timer);
        },
        getViewport: ()=>({
                width: host.win.innerWidth,
                height: host.win.innerHeight
            }),
        onViewportResize: makeOnViewportResize(host.resizeWin),
        trackPointerDrag: makeTrackPointerDrag(host.doc),
        prefersReducedMotion: ()=>host.win.matchMedia('(prefers-reduced-motion: reduce)').matches,
        scrubUrlFragment: ()=>host.win.history.replaceState(null, '', host.win.location.pathname + host.win.location.search),
        reloadAt: (pathname, locationState)=>{
            host.win.history.replaceState(toHistoryEnvelope(locationState), '', pathname);
            wiring.locationReload();
        },
        wasEnteredByBackForward: ()=>{
            var _host_perf_getEntriesByType_;
            return ((_host_perf_getEntriesByType_ = host.perf.getEntriesByType('navigation')[0]) === null || _host_perf_getEntriesByType_ === void 0 ? void 0 : _host_perf_getEntriesByType_.type) === 'back_forward';
        },
        createObjectUrl: (file)=>host.url.createObjectURL(file),
        getPathname: ()=>host.win.location.pathname,
        getHref: ()=>host.win.location.href,
        postJson: makePostJson(host.fetch),
        readPersistedLocationState: ()=>readPersistedLocationState(wiring.historyIo),
        persistLocationState: (patch)=>persistLocationState(wiring.historyIo, patch),
        crypto: {
            randomNonce: ()=>toHex(host.win.crypto.getRandomValues(new Uint8Array(32))),
            sha256Hex: async (input)=>{
                const digest = await host.win.crypto.subtle.digest('SHA-256', host.encodeUtf8(input));
                return toHex(new Uint8Array(digest));
            }
        },
        webAppleAuth: wiring.webAppleAuth
    });
